import React, { useEffect, useMemo } from "react";
import { FieldArray, FormikProps } from "formik";

// MUI
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Stack,
} from "@mui/material";
import { Add, DeleteOutline } from "@mui/icons-material";

import FormikControl from "../formik/FormikControl";
import { StyledTableCell } from "../../admin/table/Table";
import { rowfields, ObjType } from "./BidStatusSection";

import { useAppSelector, useAppDispatch } from "../../redux/hooks/redux-hooks";
import { fetchProjectCompetitors } from "../../redux/actions/ProjectCompetitorsActions";
import { createAutocompleteOptions } from "../../utility/utils";

type Props<T extends {}, K extends {}> = {
  data: T[] | undefined | null;
  formik: FormikProps<K>;
  isProjectClosed: boolean;
};

const BidWinLossTable = <T extends {}, K extends {}>({
  data,
  formik,
  isProjectClosed,
}: Props<T, K>) => {
  const dispatch = useAppDispatch();
  const allCompetitors = useAppSelector(
    (state) => state.projectCompetitors.all_ProjectCompetitors
  );

  const competitorOptions = useMemo(
    () => createAutocompleteOptions(allCompetitors, "name", "name"),
    [allCompetitors]
  );

  // Fetch all competitors
  useEffect(() => {
    dispatch(fetchProjectCompetitors());
  }, [dispatch]);

  const handleRowRemove = (
    remove: <T>(index: number) => T | undefined,
    index: number
  ) => {
    remove(index);
  };

  const handleRowAdd = (push: <R extends {}>(obj: R) => void) => {
    let isValid = true;
    const lastIndex = data ? data.length - 1 : 0;
    let dataObj: ObjType = {};
    if (data) {
      dataObj = data[lastIndex];
    }
    Object.keys(rowfields).forEach((fieldName) => {
      if (!dataObj[fieldName]) {
        isValid = false;
      }
      formik.setFieldTouched(`data[${lastIndex}].${fieldName}`, true);
    });

    if (isValid) {
      push(rowfields);
    }
  };

  return (
    <TableContainer sx={{ marginTop: "1em" }}>
      <Table>
        <TableHead>
          <TableRow>
            <StyledTableCell>Rank</StyledTableCell>
            <StyledTableCell width="25%">Bidder Name</StyledTableCell>
            <StyledTableCell>Evaluated Price (Cr)</StyledTableCell>
            <StyledTableCell>Remarks</StyledTableCell>
            {!isProjectClosed && (
              <StyledTableCell sx={{ textAlign: "center" }}>
                Action
              </StyledTableCell>
            )}
          </TableRow>
        </TableHead>

        <TableBody>
          <FieldArray name="data">
            {({ remove, push }) => {
              return (
                <>
                  {data?.map((row, index) => (
                    <TableRow key={index} sx={{ verticalAlign: "top" }}>
                      <TableCell
                        size="small"
                        sx={{
                          paddingLeft: 0,
                          borderBottom: "none",
                        }}
                      >
                        <FormikControl
                          control="input"
                          type="text"
                          size="small"
                          fullWidth
                          name={`data[${index}].rank`}
                          placeholder="Rank"
                          disabled={isProjectClosed}
                        />
                      </TableCell>
                      <TableCell
                        size="small"
                        sx={{
                          borderBottom: "none",
                        }}
                      >
                        <FormikControl
                          control="autocomplete"
                          disableClearable
                          freeSolo
                          options={competitorOptions}
                          name={`data[${index}].bidderName`}
                          size="small"
                          fullWidth
                          sx={{ flex: 1, flexBasis: 151 }}
                          placeholder="Select Bidder Name"
                          disabled={isProjectClosed}
                        />
                      </TableCell>
                      <TableCell
                        size="small"
                        sx={{
                          borderBottom: "none",
                        }}
                      >
                        <FormikControl
                          control="input"
                          type="text"
                          size="small"
                          fullWidth
                          name={`data[${index}].evaluatedPrice`}
                          placeholder="Evaluated Price"
                          disabled={isProjectClosed}
                        />
                      </TableCell>
                      <TableCell
                        size="small"
                        sx={{
                          borderBottom: "none",
                        }}
                      >
                        <FormikControl
                          control="input"
                          multiline
                          size="small"
                          fullWidth
                          maxRows={1}
                          name={`data[${index}].remarks`}
                          placeholder="Remarks"
                          disabled={isProjectClosed}
                        />
                      </TableCell>
                      {!isProjectClosed && (
                        <TableCell
                          size="small"
                          sx={{
                            borderBottom: "none",
                          }}
                        >
                          <Stack direction="row">
                            {data.length !== 1 && (
                              <IconButton
                                onClick={() => handleRowRemove(remove, index)}
                              >
                                <DeleteOutline color="error" />
                              </IconButton>
                            )}
                            {data.length - 1 === index && (
                              <IconButton onClick={() => handleRowAdd(push)}>
                                <Add color="primary" />
                              </IconButton>
                            )}
                          </Stack>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </>
              );
            }}
          </FieldArray>
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default BidWinLossTable;
